import { Shape } from 'three'
import * as THREE from 'three'
import { useVerandaSelector } from '../../../../..'
import { Helper } from '../../../../../classes/helper'
import { useMeasures } from '../../../../../hooks/state/useMeasures'
import { useRoof } from '../../../../../hooks/state/useRoof'
import { CachedMeshPhysicalMaterial } from '../../../../material/cachedMeshPhysicalMaterial'
import { attachedBar } from '../../model/helper/verandaDimensions'
import { LedSpot } from '../../../../utility/light/LedSpot'

function createSlatShape(width: number, height: number, grooveDepth: number) {
  const shape = new Shape()

  shape.moveTo(height / 2, width / 2)
  shape.lineTo(grooveDepth, width / 2)
  shape.lineTo(0, width / 2 - grooveDepth)
  shape.lineTo(0, -width / 2 + grooveDepth)
  shape.lineTo(grooveDepth, -width / 2)
  shape.lineTo(height, -width / 2)
  shape.lineTo(height, -width / 2 - grooveDepth)
  shape.lineTo(height - 0.015, -width / 2 - grooveDepth)
  shape.lineTo(height, -width / 2 - 0.002)
  shape.lineTo(height - 0.015, -width / 2)
  shape.lineTo(height, -width / 2)
  shape.lineTo(height, width / 2 - 0.025)
  shape.lineTo(0.001, width / 2 - grooveDepth)
  shape.lineTo(0.001, width / 2)
  shape.lineTo(height / 2, width / 2)
  return shape
}

function createSlats(
  slatAngle: boolean,
  slatWidth: number,
  spaceBetweenSlatWidth: number,
  spaceBetweenSlatLength: number,
  isQubus = false
) {
  const amount = Math.ceil(spaceBetweenSlatWidth / slatWidth)
  const position = slatAngle
    ? [0, slatWidth / 2, -spaceBetweenSlatLength / 2]
    : [0, -(slatWidth * amount) / 2 + slatWidth / 2, 0]
  const posQubus = isQubus
    ? position
    : slatAngle
    ? [0, 0.04, -spaceBetweenSlatLength / 2 + 0.025]
    : [0, -(slatWidth * amount) / 2 + slatWidth / 2, 0]
  const slats = {
    amount: amount,
    position: posQubus as [number, number, number],
  }

  return slats
}

export const Slats = (): JSX.Element => {
  const { metalColor, slatAngle, slatMoveIn, unit } = useRoof()
  const {
    verandaTyp,
    depth,
    userWidth,
    roofLogWidth,
    abstandLed,
    isQubus,
    attachedSize,
  } = useMeasures()
  const shadingMode = useVerandaSelector((state) => state.scene.shadingMode)
  const extrudedRound = new THREE.CylinderGeometry(0.025, 0.025, 0.002, 36)
  const glowYellow = new THREE.MeshStandardMaterial({
    color: new THREE.Color(7, 4, 0.5),
    toneMapped: false,
  })
  const slatMaxWidth = 0.15
  const slatDepht = 0.02

  const spaceBetweenSlatLength = isQubus
    ? slatAngle
      ? userWidth - verandaTyp.log.width
      : depth - verandaTyp.log.depth * 2
    : slatAngle
    ? userWidth - verandaTyp.roofBar.depth * 2
    : roofLogWidth

  const spaceBetweenSlatWidth = isQubus
    ? slatAngle
      ? depth - verandaTyp.log.depth * 2
      : userWidth - verandaTyp.log.width - slatDepht / 2
    : slatAngle
    ? roofLogWidth
    : userWidth - verandaTyp.roofBar.depth * 2

  const extrudeSettings = {
    steps: 1,
    depth: spaceBetweenSlatLength,
    bevelEnabled: false,
  }

  const lamellenAnzahl = Math.ceil(spaceBetweenSlatWidth / slatMaxWidth) - 1
  const lamellenBreite = spaceBetweenSlatWidth / lamellenAnzahl
  const anzahlLamellen = Math.ceil(spaceBetweenSlatWidth / lamellenBreite)
  const slats = createSlats(
    slatAngle!,
    lamellenBreite,
    spaceBetweenSlatWidth,
    spaceBetweenSlatLength,
    isQubus
  )
  const slatShape = createSlatShape(lamellenBreite, slatDepht, 0.005)

  const rot = shadingMode ? 0 : 90
  const singleSlatPosition =
    slatMoveIn === '1' && !shadingMode ? slatDepht + 0.02 : lamellenBreite
  const anzahlReihen = anzahlLamellen / abstandLed
  const totalLeds = anzahlReihen * unit
  const freieLamellen = anzahlLamellen - totalLeds * abstandLed

  const freeLamellenStart = Math.floor(freieLamellen / 2) - 1
  const freeLamellenEnd = freieLamellen - freeLamellenStart + 1
  const ledPositions = [...Array(anzahlLamellen)].map((_, index) => {
    return (
      index >= freeLamellenStart &&
      (index - freeLamellenStart) % abstandLed === 0 &&
      index < anzahlLamellen - freeLamellenEnd
    )
  })

  return (
    <group
      position={[
        0,
        isQubus ? -lamellenBreite / 2 : verandaTyp.roofBar.width,
        isQubus ? 0 : attachedSize.z / 2,
      ]}
      rotation={[
        0,
        Helper.convertAngleToRadiant(slatAngle ? 90 : 0),
        Helper.convertAngleToRadiant(90),
      ]}
      castShadow
    >
      <group position={slats.position} castShadow>
        {[...Array(anzahlLamellen)].map((_, index) => {
          return (
            <mesh
              key={`Slats${index}`}
              rotation={[
                Helper.convertAngleToRadiant(0),
                Helper.convertAngleToRadiant(0),
                Helper.convertAngleToRadiant(rot),
              ]}
              castShadow={true}
              receiveShadow={true}
              position={[0, singleSlatPosition * index, isQubus ? 0 : -0.025]}
            >
              <extrudeGeometry args={[slatShape, extrudeSettings]} />

              <CachedMeshPhysicalMaterial
                color={metalColor}
                roughness={0.8}
                metalness={0.2}
              />

              {ledPositions[index] && (
                <group
                  rotation={[0, 0, Helper.convertAngleToRadiant(-90)]}
                  position={[shadingMode ? 0.01 : 0.01, 0, 0]}
                >
                  {[...Array(unit)].map((_, i) => {
                    return (
                      <mesh
                        key={`unit${i}`}
                        castShadow={false}
                        position={[
                          0.015,
                          slatAngle ? 0 : 0,
                          slatAngle
                            ? spaceBetweenSlatLength / (unit + 1) +
                              (i * spaceBetweenSlatLength) / (unit + 1)
                            : roofLogWidth / (unit + 1) +
                              (i * roofLogWidth) / (unit + 1),
                        ]}
                      >
                        <LedSpot
                          geometry={extrudedRound}
                          material={glowYellow}
                          position={[0, -slatDepht / 2, 0]}
                          visible={ledPositions[index]}
                        />
                      </mesh>
                    )
                  })}
                </group>
              )}
            </mesh>
          )
        })}
      </group>
    </group>
  )
}
